import React, { useState } from 'react';
import { StickyBar, DocInfoBar, DocMenu, DocHeader } from 'components';
import { useDocState, useDate, useDocCloseDate } from 'hooks';
import { v4 as uuid_v4 } from 'uuid';
import dayjs from 'dayjs';
import { gqlClient } from 'gql-client';
import { loader } from 'graphql.macro';
import { DateBox, Form, LoadPanel, TextArea, ValidationGroup } from 'devextreme-react';
import { ColCountByScreen, GroupItem, Label, SimpleItem } from 'devextreme-react/form';
import { FORM_STYLING_MODE, orderDocStatuses } from 'app-constants';
import { WorkersGrid } from './workersGrid';
import { WorktimeDoc, WorktimeRow, WorktimeSaveDoc, LegendItem } from './worktimeTypes';
import { custom } from 'devextreme/ui/dialog';
import { ValueChangedEvent } from 'devextreme/ui/date_box';
import { ChangeEvent as TextAreaChangeEvent } from 'devextreme/ui/text_area';
import { docValidationMsg } from 'messages';
import { cloneDeep } from 'lodash';
import { MenuItem, TDataBaseObjectClasseNames } from 'types/otkTypes';
import { jfilt } from 'utils/jfilt';

const getDoc = loader('./gql/getWorkTime.gql');
const updateWorkTime = loader('./gql/updateWorkTime.graphql');
const dsGetWorkTimes = loader('../../datasources/dsWorkTimes/dsGetWorkTimes.gql');

const CLASS_NAME: TDataBaseObjectClasseNames = 'doc.work_time';

const legend: LegendItem[] = [
  { value: 'в', caption: 'Відпустка' },
  { value: 'тн', caption: 'Лікарняний' },
  { value: 'ін', caption: 'Мобілізація' },
  { value: 'вд', caption: 'Відрядження' },
  { value: 'на', caption: 'Відпустка без збереження' },
  { value: 'нз', caption: 'Невихід з нез\'ясованих. причин' },
];

const EMPTY_PRINT_ITEMS: MenuItem[] = [];
const MAX_DAYS_IN_MONTH = 31;

const calculateStatus = (data: WorktimeDoc) => (data?.number_doc ? orderDocStatuses.ACCEPTED : orderDocStatuses.NEW);

const confirmClearWorktable = (): Promise<boolean> => custom({
  showTitle: false,
  messageHtml: `
    <p style="text-align:center;">
    <b>Таблиця буде очищена<br/>
    Продовжити?
    </b></p>`,
  buttons: [
    { text: 'Так', onClick: () => true },
    { text: 'Ні', onClick: () => false },
  ],
}).show();

const legendGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateRows: `repeat(${Math.ceil(legend.length / 2)}, auto)`,
  gridAutoFlow: 'column',
  columnGap: 16,
  border: '1px solid #d3d3d3',
  borderRadius: 4,
  padding: '4px 8px',
};

const legendItemStyle: React.CSSProperties = {
  display: 'flex',
  gap: 6,
  padding: 2,
};

const Legend = () => (
  <div style={legendGridStyle}>
    {legend.map((item) => (
      <div key={item.value} style={legendItemStyle}>
        <span style={{ minWidth: 24 }}>{item.value}</span>
        <span>{item.caption}</span>
      </div>
    ))}
  </div>
);

export const WorkTime = () => {
  const { formatDate, startOfMonth, startOfDay } = useDate();
  const [ workersData, setWorkersData ] = useState<WorktimeRow[]>([]);

  const [ data, setData, { loading, isDocNew } ] = useDocState<WorktimeDoc>({
    className: CLASS_NAME,
    defaultData: {
      date: startOfDay(),
      period_reg: startOfMonth(),
      number_doc: '',
      class_name: CLASS_NAME,
      note: '',
    },
    load: async(id?: string): Promise<WorktimeDoc> => {
      if (id === 'new') return data;
      return gqlClient.query({ query: getDoc, variables: { ref: id } })
        .then((response) => {
          const doc = response?.data?.worktime?.[0];
          if (!doc?.ref) throw new Error();
          return doc;
        });
    },
    update: (state, data) => ({ ...state, ...data }),
  });

  const docMonth = startOfMonth(data.date);
  const currentMonth = startOfMonth();
  const graceMonth = startOfMonth(dayjs().subtract(7, 'day')); // дозволяє редагувати документ попереднього місяця протягом перших 7 днів нового

  const isDocClosed = docMonth !== currentMonth && docMonth !== graceMonth;

  const closeDate = useDocCloseDate(CLASS_NAME);
  const isClosedByPeriod = startOfMonth(data.period_reg) < startOfDay(closeDate);

  const docReadOnly = data.protected || isDocClosed || isClosedByPeriod;

  const validateForm = async() => {

    let errorMessage = '';

    // check for existed doc for period_reg
    const existedDocs = await gqlClient.query({
      query: dsGetWorkTimes,
      variables: {
        requireTotalCount: false,
        branches: [], //check only for main branch
        jfilt: jfilt()
          .add('period_reg', '=', formatDate(startOfMonth(data.period_reg)))
          .and()
          .add('ref', '<>', `${data?.ref ?? ''}`)
          .build(),
      },
    }).then((response) => response?.data?.list ?? []);

    if (existedDocs.length) {
      errorMessage += docValidationMsg(`Існує документ № ${existedDocs[0].number_doc} для данного періоду`);
    }

    if (docMonth < startOfMonth(data.period_reg)) {
      errorMessage += docValidationMsg('Заборона створення документу в майбутній період');
    }

    return errorMessage;
  };

  const handleDocumentSave = async(): Promise<string> => {

    const err = await validateForm();
    if (err) return Promise.reject(err);

    const doctosave: WorktimeSaveDoc = cloneDeep(data);
    doctosave.date = formatDate(doctosave.date);
    const saveUuid = uuid_v4();
    if (isDocNew) {
      doctosave._id = `${CLASS_NAME}|${saveUuid}`;
      doctosave.class_name = CLASS_NAME;
    }

    const daysInMonth = Array.from({ length: MAX_DAYS_IN_MONTH }, (_, i) => i + 1);

    const worktable: WorktimeRow[] = workersData.map((row) => ({
      person_id: row.person_id,
      person_fio: row.person_fio,
      positions: row.positions,
      graphic_id: row.graphic_id,
      graphic_name: row.graphic_name,
      hours: daysInMonth
        .filter((day) => `d_${day}` in row) // лише дні, заповнені в гріді
        .map((day) => ({ day, hours: row[`d_${day}`] })), // у формат { day, hours } для збереження
    }));
    doctosave.worktable = worktable;
    doctosave.period_reg = formatDate(doctosave.period_reg);
    doctosave.hours = undefined;
    doctosave.savedByLab = true;
    return gqlClient
      .mutate({
        mutation: updateWorkTime,
        variables: { input: doctosave }
      })
      .then(() => saveUuid);
  };

  const changeReq = (e: TextAreaChangeEvent) => {
    const target = e.event?.target as HTMLTextAreaElement | undefined;
    setData((prevState) => ({ ...prevState, [e.element.id]: target?.value }));
  };

  const handlePeriodChange = async(e: ValueChangedEvent) => {
    const newPeriod = startOfMonth(e.value);
    if (!e.event || startOfMonth(e.previousValue) === newPeriod) return;

    const hasWorktable = !!data?.worktable?.length;
    if (hasWorktable && !(await confirmClearWorktable())) return;

    setData((prev) => ({
      ...prev,
      period_reg: newPeriod,
      worktable: hasWorktable ? [] : prev.worktable
    }));
  };

  const status = calculateStatus(data);

  return (
    <React.Fragment>
      <StickyBar>
        <DocInfoBar
          name='Табель робочого часу'
          data={{ date: data.date, number: data.number_doc }}
          loading={loading}
          isNew={isDocNew}>
          {data.protected && (
            <div className={'otk-tag otk-status-warning'}>
              <div className='dx-icon-warning'></div>
              &nbsp;Документ тільки для перегляду
            </div>
          )}
          <div className={`otk-tag otk-status-${status.status}`}>
            {status.text}
          </div>
        </DocInfoBar>

        <DocMenu
          isDocNew={isDocNew}
          allowSaving={!docReadOnly}
          onSave={handleDocumentSave}
          printItems={EMPTY_PRINT_ITEMS}
          // template for later use
          // printItems={[
          //   {
          //     text: 'Рахунок',
          //     url: `${API_HOST}/printform/${id}/inv`,
          //     disabled: !data.number_doc,
          //   },
          // ]}
        />
      </StickyBar>

      <LoadPanel visible={loading} />
      <ValidationGroup>
        <div className='content-block otk-content-block'>
          <div className={'otk-doc-container otk-doc-form otk-doc-form-large dx-card'}>
            <Form labelLocation='top' formData={data}>
              <GroupItem>
                <ColCountByScreen xs={1} sm={2} md={2} lg={2} />

                <GroupItem colSpan={1}>
                  <ColCountByScreen xs={1} sm={1} md={1} lg={1} />
                  <SimpleItem colSpan={1} isRequired>
                    <Label text='Період' location='left' />
                    <DateBox
                      value={data.period_reg}
                      displayFormat='monthAndYear'
                      onValueChanged={handlePeriodChange}
                    />
                  </SimpleItem>
                  <GroupItem colSpan={1}>
                    <SimpleItem render={() => <Legend />}>
                      <Label text='Позначення (права кнопка мишки)'/>
                    </SimpleItem>
                  </GroupItem>
                </GroupItem>

                <SimpleItem>
                  <DocHeader number_doc={data.number_doc} date={data.date} statusText={status.text}/>
                </SimpleItem>
              </GroupItem>
              <SimpleItem>
                <WorkersGrid
                  data={data}
                  value={workersData}
                  onChange={setWorkersData}
                  legend={legend}
                  docReadOnly={docReadOnly}
                />
              </SimpleItem>

              <GroupItem>
                <ColCountByScreen xs={1} sm={8} md={8} lg={10} />
                <SimpleItem colSpan={8}>
                  <Label text='Коментар' />
                  <TextArea
                    id='note'
                    readOnly={docReadOnly}
                    value={data.note}
                    stylingMode={FORM_STYLING_MODE}
                    hint='коментар'
                    onChange={changeReq}
                  />
                </SimpleItem>
              </GroupItem>
            </Form>
          </div>
        </div>
      </ValidationGroup>
    </React.Fragment>
  );
};
